
import Vue from 'vue';

const state = {
  notes: null,
};

const getters = {
  userId: (state, getters, rootState, rootGetters) => rootGetters['users/userId'],
  getNoteRef: state => ({ noteId, relatedCollectionId }) => {
    if (!noteId) return false;

    // If we do have a noteId, first try to get the note ref based on the specification (i.e. checking a particular collection).
    // If this fails or we do not have a specification, go through all collections to find the note ref.
    let requestedNote = null;

    // From a particular collection
    if (!requestedNote && relatedCollectionId) requestedNote = state.notes[relatedCollectionId].find(note => note.id === noteId);

    // Check all collections
    if (!requestedNote) {
      Object.keys(state.notes).some((collectionId) => {
        requestedNote = state.notes[collectionId].find(note => note.id === noteId);
        return !!requestedNote;
      });
    }

    return requestedNote;
  },
  notesByStars: (state) => {
    const stars = {
      1: [],
      2: [],
      3: [],
    };
    Object.values(state.notes).forEach((collection) => {
      collection.forEach((note) => {
        if (!note.stars) return;
        stars[note.stars].push(note);
      });
    });
    return stars;
  },
};

const actions = {
  addCollectionNotes({ commit }, { noteId, collectionId, dateCreated, dateLastModified, title, section, notes }) {
    commit('AddCollectionNotes', { collectionId, noteId, type: 'note', title, section, notes, dateCreated, dateLastModified });
  },
  updateCollectionNotes({ dispatch, commit, getters }, { collectionId: receivedCollectionId, noteId, title, notes, stars, section, dateLastModified }) {
    if (!noteId || !dateLastModified) {
      dispatch('notifications/setError', { error: 'Error: failed to update collection notes (validation failed)' }, { root: true });
      return;
    }

    // In case we did not receive a collectionId, look it up
    let collectionId = receivedCollectionId;
    if (!collectionId) {
      const noteRef = getters.getNoteRef({ noteId });
      collectionId = noteRef.relatedCollectionId;
    }

    commit('UpdateCollectionNotes', { collectionId, noteId, title, notes, stars, section, dateLastModified });

    dispatch('sync/enqueueCollectionNote', { action: 'update', collectionId, noteId, title, notes, stars, section, dateLastModified }, { root: true });
  },
  moveCollectionNotes({ dispatch, commit, getters }, { collectionId: receivedCollectionId, noteId, newCollectionId, dateLastModified }) {
    if (!noteId || !newCollectionId) {
      dispatch('notifications/setError', { error: 'Error: failed to update collection notes (validation failed)' }, { root: true });
      return;
    }

    // In case we did not receive a collectionId, look it up
    let collectionId = receivedCollectionId;
    if (!collectionId) {
      const noteRef = getters.getNoteRef({ noteId });
      collectionId = noteRef.relatedCollectionId;
    }

    commit('MoveCollectionNotes', { collectionId, noteId, newCollectionId, dateLastModified });

    dispatch('sync/enqueueCollectionNote', { action: 'update', collectionId, noteId, newCollectionId, dateLastModified }, { root: true });
  },
  deleteCollectionNotes({ state, dispatch, getters, commit, rootState }, { collectionId: receivedCollectionId, noteId }) {
    if (!noteId) {
      dispatch('notifications/setError', { error: 'Error: failed to delete collection notes (validation failed)' }, { root: true });
      return;
    }

    // In case we did not receive a collectionId, look it up
    let collectionId = receivedCollectionId;
    if (!collectionId) {
      const noteRef = getters.getNoteRef({ noteId });
      collectionId = noteRef.relatedCollectionId;
    }

    const deleteIndex = state.notes[collectionId].findIndex(note => note.id === noteId);
    if (deleteIndex < 0) {
      // This is a temporary fix for deleting notes without providing the right collectionId. This can happen when one searches for notes, then uses batch select to select
      // and delete notes, for that uses selectedCollection to determine which collectionId to send to this function.
      // Workaround for now, better fix later. (// To Do:)
      // Also: not tested yet since you cannot yet search for notes :p
      const noteToDelete = getters.getNoteRef({ noteId });
      const { relatedCollectionId: fixedCollectionId } = noteToDelete;
      const fixedDeleteIndex = state.notes[fixedCollectionId].findIndex(note => note.id === noteId);
      if (fixedDeleteIndex < 0) {
        // For some reason still cannot find the note. Show an error.
        dispatch('notifications/setError', { error: 'Error: could not delete collection note, for could not find it in the specified collection' }, { root: true });
        return;
      }

      commit('DeleteCollectionNotes', { collectionId: fixedCollectionId, deleteIndex });
      commit('appState/DeleteFromHistory', `/note/${noteId}`, { root: true });
      commit('appState/CloseOpenItem', { id: noteId, type: 'note' }, { root: true });
      commit('users/UnpinItem', { id: noteId, type: 'note' }, { root: true });
      dispatch('sync/enqueueUserUpdate', { settings: rootState.users.settings }, { root: true });

      dispatch('sync/enqueueCollectionNote', { action: 'delete', collectionId: fixedCollectionId, noteId }, { root: true });
      return;
    }

    commit('DeleteCollectionNotes', { collectionId, deleteIndex });
    commit('appState/DeleteFromHistory', `/note/${noteId}`, { root: true });
    commit('appState/CloseOpenItem', { id: noteId, type: 'note' }, { root: true });
    commit('users/UnpinItem', { id: noteId, type: 'note' }, { root: true });
    dispatch('sync/enqueueUserUpdate', { settings: rootState.users.settings }, { root: true });

    dispatch('sync/enqueueCollectionNote', { action: 'delete', collectionId, noteId }, { root: true });
  },
  deleteAllNotesFromCollection({ state, commit, dispatch, rootState }, collectionId) {
    const notes = state.notes[collectionId];
    if (notes) {
      // Delete notes themselves and delete all history entries
      commit('DeleteAllNotesFromCollection', collectionId);
      notes.forEach((note) => {
        commit('appState/DeleteFromHistory', `/note/${note.id}`, { root: true });
        commit('appState/CloseOpenItem', { id: note.id, type: 'note' }, { root: true });
        commit('users/UnpinItem', { id: note.id, type: 'note' }, { root: true });
      });
      dispatch('sync/enqueueUserUpdate', { settings: rootState.users.settings }, { root: true });
    }
  },
};

const mutations = {
  SetNotes(state, payload) {
    state.notes = payload;
  },
  AddCollectionNotes(state, { collectionId, noteId, type, title = null, section, notes, dateCreated, dateLastModified }) {
    // to do: change all 'collectionId' params to 'relatedCollectionId' (also in actions and stuff)
    if (!state.notes[collectionId]) Vue.set(state.notes, collectionId, []);
    state.notes[collectionId].push({ id: noteId, type, relatedCollectionId: collectionId, title, section, notes, dateCreated, dateLastModified, stars: 0 });
  },
  UpdateCollectionNotes(state, { collectionId, noteId, title, notes, stars, section, dateLastModified }) {
    const noteRef = state.notes[collectionId].find(note => note.id === noteId);
    if (title || title === 0) noteRef.title = title;
    if (notes || notes === 0) noteRef.notes = notes;
    if (stars || stars === 0) noteRef.stars = stars;
    if (section || section === null) noteRef.section = section;
    noteRef.dateLastModified = dateLastModified;
  },
  MoveCollectionNotes(state, { collectionId, noteId, newCollectionId, dateLastModified }) {
    const index = state.notes[collectionId].findIndex(note => note.id === noteId);

    // Create entry for collection if necessary
    if (!state.notes[newCollectionId]) Vue.set(state.notes, newCollectionId, []);

    // Push to collection
    const updatedNoteObj = Object.assign(state.notes[collectionId][index], { relatedCollectionId: newCollectionId, dateLastModified });
    state.notes[newCollectionId].push(updatedNoteObj);
    Vue.delete(state.notes[collectionId], index);
  },
  DeleteCollectionNotes(state, { collectionId, deleteIndex }) {
    Vue.delete(state.notes[collectionId], deleteIndex);
  },
  DeleteAllNotesFromCollection(state, collectionId) {
    Vue.delete(state.notes, collectionId);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
